import {
  Alert,
  Flex,
  Form,
  Input,
  InputNumber,
  Collapse,
  Modal,
  Progress,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Button } from '@/components/button/button';
import { AgentStatusBadge } from '@/components/agent-status-badge/agent-status-badge';
import {
  useAgent,
  useUpdateAgent,
  useDeleteAgent,
  usePublishAgent,
  useUnpublishAgent,
  useAgentUsage,
  useTestCall,
  useAddKbDocument,
  useRemoveKbDocument,
  useCheckKbStatus,
  useTransferableDocuments,
  useTransferKb,
  useLanguageVoices,
  useUpdateLanguageVoices,
  useVoiceHealth,
  useSyncAgent,
  useTransferAnnouncements,
  useGenerateTransferAnnouncements,
  useFillerClips,
  useAudioClipTexts,
  useUpdateAudioClipText,
  useResetAudioClipText,
} from '@/hooks/use-agents/use-agents';
import { useCompanyPhoneNumbers } from '@/hooks/use-phone-numbers/use-phone-numbers';
import { useUser } from '@/hooks/use-user/use-user';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AgentDocument, AudioClipText, AudioClipType, ElevenLabsModel, ELEVEN_LABS_MODELS, FillerClip, LanguageVoice, TransferableDocument, TransferAnnouncement } from '@/services/agents-service/agents.service.types';
import { getFillerClipPreviewBlob } from '@/services/agents-service/agents.service';
import { DeleteOutlined, EyeOutlined, ImportOutlined, SoundOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';
import { VoiceField } from '@/components/voice-field/voice-field';
import { languageName } from '@/utils/language-name';
import { formatNumber } from '@/i18n/format';
import { useLanguageOptions } from '../agents.constants';
import styles from './agent-detail.module.scss';

const KB_STATUS_MAP: Record<string, string> = {
  ready: 'published',
  uploaded: 'agent_saved',
  failed: 'publish_failed',
  pending: 'draft',
};

type AgentsOutletContext = { companyId: number | undefined };

// Inline preview button for the filler clips status table. Fetches the
// µ-law-as-WAV blob via the authenticated CRM proxy, creates a blob URL,
// and plays it. Audio is cached per-instance so a second click of the
// same row doesn't re-fetch.
function FillerClipPreviewButton({
  agentId,
  companyId,
  voiceId,
  langCode,
}: {
  agentId: string;
  companyId: number;
  voiceId: string;
  langCode: string;
}) {
  const { t } = useTranslation('agents');
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  // Revoke any cached object URL on unmount so the blob is garbage-
  // collectable. Without this each preview button leaks one blob URL
  // for the lifetime of the document.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  const toggle = useCallback(async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }
    if (!urlRef.current) {
      setLoading(true);
      try {
        const blob = await getFillerClipPreviewBlob(agentId, {
          company_id: companyId,
          voice_id: voiceId,
          lang_code: langCode,
        });
        urlRef.current = URL.createObjectURL(blob);
      } catch {
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    const audio = new Audio(urlRef.current!);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.play();
    setPlaying(true);
  }, [agentId, companyId, voiceId, langCode, playing]);

  return (
    <Button
      size="small"
      icon={<SoundOutlined />}
      loading={loading}
      onClick={toggle}
      aria-label={playing ? t('detail.audio.previewStop') : t('detail.audio.previewPlay')}
    />
  );
}

export function AgentDetail() {
  const { t } = useTranslation(['agents', 'common']);
  const languageOptions = useLanguageOptions();
  const { agentId = '' } = useParams();
  const navigate = useNavigate();
  const { companyId: contextCompanyId } = useOutletContext<AgentsOutletContext>();
  const { userData } = useUser();
  const companyId = contextCompanyId ?? userData?.company?.id ?? userData?.company_id;
  const { languageVoices } = useLanguageVoices(companyId);
  const { updateLanguages, isPending: isSavingLanguages } = useUpdateLanguageVoices(companyId);
  const { health: voiceHealth, workspaceTotal, error: voiceHealthError } = useVoiceHealth(agentId, companyId);
  const { agent, isPending: isLoadingAgent } = useAgent(agentId, companyId);
  const { update, isPending: isUpdating } = useUpdateAgent(agentId, companyId);
  const { remove, isPending: isDeleting } = useDeleteAgent(companyId);
  const { publish, isPending: isPublishing } = usePublishAgent(agentId, companyId);
  const { unpublish, isPending: isUnpublishing } = useUnpublishAgent(agentId, companyId);
  const { sync, isPending: isSyncing } = useSyncAgent(agentId, companyId);
  const { usage } = useAgentUsage(agentId, companyId);
  const { makeTestCall, isPending: isCalling } = useTestCall(agentId, companyId);
  const { addText, addUrl, addFile, isPending: isAddingDoc } = useAddKbDocument(agentId, companyId);
  const { remove: removeDoc, isPending: isRemovingDoc } = useRemoveKbDocument(agentId, companyId);
  const { checkStatus, isPending: isCheckingStatus } = useCheckKbStatus(agentId, companyId);
  const { phoneNumbers } = useCompanyPhoneNumbers(companyId);
  const { documents: transferableDocs, isPending: isLoadingDocs } = useTransferableDocuments(agentId, companyId);
  const { transfer: transferKb, isPending: isTransferring } = useTransferKb(agentId, companyId);
  const { announcements, isPending: isLoadingAnnouncements, refetch: refetchAnnouncements } = useTransferAnnouncements(agentId, companyId);
  const { generate: generateAnnouncements, isPending: isGenerating } = useGenerateTransferAnnouncements(agentId, companyId);
  const { clips: fillerClips, isPending: isLoadingFillerClips, refetch: refetchFillerClips } = useFillerClips(agentId, companyId);
  const { texts: audioClipTexts } = useAudioClipTexts(agentId, companyId);
  const { updateText: saveAudioClipText, isPending: isSavingAudioClipText } = useUpdateAudioClipText(agentId, companyId);
  const { resetText: resetAudioClipText, isPending: isResettingAudioClipText } = useResetAudioClipText(agentId, companyId);

  const [draftDisclaimer, setDraftDisclaimer] = useState<string>('');
  const [draftTransferring, setDraftTransferring] = useState<string>('');
  const [draftFiller, setDraftFiller] = useState<string>('');

  useEffect(() => {
    audioClipTexts?.forEach((entry) => {
      if (entry.clip_type === 'disclaimer') setDraftDisclaimer(entry.text);
      if (entry.clip_type === 'transferring') setDraftTransferring(entry.text);
      if (entry.clip_type === 'filler') setDraftFiller(entry.text);
    });
  }, [audioClipTexts]);

  const [configForm] = Form.useForm();
  const [kbForm] = Form.useForm();
  const [testCallForm] = Form.useForm();
  const [kbMode, setKbMode] = useState<'text' | 'url' | 'file'>('text');
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [viewingDoc, setViewingDoc] = useState<AgentDocument | null>(null);
  const [localLanguages, setLocalLanguages] = useState<LanguageVoice[]>([]);
  // Tabs are controlled so the engine-summary banner on the Languages tab
  // can switch the user back to the General tab when they click "Change".
  const [activeTab, setActiveTab] = useState<string>('config');

  useEffect(() => {
    if (agent) {
      configForm.setFieldsValue({
        name: agent.name,
        system_prompt: agent.system_prompt,
        first_message: agent.first_message,
        language: agent.language,
        voice_id: agent.voice_id,
        eleven_labs_model: agent.eleven_labs_model,
        twilio_phone_number_id: agent.twilio_phone_number_id,
        monthly_budget_minutes: agent.monthly_budget_minutes,
        budget_alert_threshold: agent.budget_alert_threshold,
        budget_action: agent.budget_action,
        vad_threshold: (agent.voice_settings as Record<string, number>)?.vad_threshold ?? 0.6,
        min_speech_duration_ms: (agent.voice_settings as Record<string, number>)?.min_speech_duration_ms ?? 250,
      });
    }
  }, [agent, configForm]);

  useEffect(() => {
    if (languageVoices) setLocalLanguages(languageVoices);
  }, [languageVoices]);

  const phoneOptions =
    phoneNumbers?.map((num) => ({
      label: num.friendly_name || num.phone_number,
      value: num.id,
    })) || [];

  const modelOptions = ELEVEN_LABS_MODELS.map((value) => ({
    value,
    label: t(`agents:models.${value}`),
  }));

  const handleSaveConfig = (values: Record<string, unknown>) => {
    update(
      {
        name: values.name as string,
        system_prompt: values.system_prompt as string,
        first_message: values.first_message as string,
        language: values.language as string,
        voice_id: values.voice_id as string,
        eleven_labs_model: (values.eleven_labs_model as ElevenLabsModel | null) ?? null,
        twilio_phone_number_id: values.twilio_phone_number_id as number | null,
        monthly_budget_minutes: values.monthly_budget_minutes as number | null,
        budget_alert_threshold: values.budget_alert_threshold as number,
        budget_action: values.budget_action as string,
        voice_settings: {
          ...((agent?.voice_settings ?? {}) as Record<string, unknown>),
          vad_threshold: values.vad_threshold as number,
          min_speech_duration_ms: values.min_speech_duration_ms as number,
        },
      },
      { onSuccess: () => message.success(t('agents:detail.messages.updated')) }
    );
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('agents:detail.delete.title'),
      content: t('agents:detail.delete.content'),
      okText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
      okType: 'danger',
      onOk: () => {
        remove(Number(agentId), {
          onSuccess: () => {
            message.success(t('agents:detail.messages.deleted'));
            navigate('/agents');
          },
        });
      },
    });
  };

  const handlePublish = () => {
    publish(undefined, {
      onSuccess: () => message.success(t('agents:detail.messages.publishStarted')),
      onError: () => message.error(t('agents:detail.messages.publishFailed')),
    });
  };

  const handleUnpublish = () => {
    unpublish(undefined, {
      onSuccess: () => message.success(t('agents:detail.messages.unpublished')),
      onError: () => message.error(t('agents:detail.messages.unpublishFailed')),
    });
  };

  const handleAddKbDoc = (values: Record<string, unknown>) => {
    message.info(t('agents:detail.kb.uploadingDocument'));
    if (kbMode === 'text') {
      addText(
        { name: values.kb_name as string, content: values.kb_content as string },
        { onSuccess: () => { message.success(t('agents:detail.kb.textAdded')); kbForm.resetFields(); } }
      );
    } else if (kbMode === 'url') {
      addUrl(
        { name: values.kb_name as string, url: values.kb_url as string },
        { onSuccess: () => { message.success(t('agents:detail.kb.urlAdded')); kbForm.resetFields(); } }
      );
    }
  };

  const handleFileUpload = (file: File) => {
    message.info(t('agents:detail.kb.uploadingFile'));
    addFile(
      { file, name: file.name },
      { onSuccess: () => message.success(t('agents:detail.kb.fileUploaded')) }
    );
    return false;
  };

  const handleTestCall = (values: Record<string, unknown>) => {
    makeTestCall(values.phone_number as string, {
      onSuccess: () => message.success(t('agents:detail.testCall.started')),
      onError: () => message.error(t('agents:detail.testCall.failed')),
    });
  };

  const kbColumns: ColumnsType<AgentDocument> = [
    { title: t('agents:labels.name'), dataIndex: 'source_name', key: 'source_name' },
    {
      title: t('agents:detail.kb.type'),
      dataIndex: 'source_type',
      key: 'source_type',
      render: (sourceType: AgentDocument['source_type']) => t(`agents:detail.kb.sourceTypes.${sourceType}`),
    },
    {
      title: t('agents:labels.status'),
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <AgentStatusBadge status={KB_STATUS_MAP[s] || 'publishing'} />,
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Flex gap={4}>
          {record.source_content && (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setViewingDoc(record)}
              title={t('agents:detail.kb.viewContent')}
            />
          )}
          {record.status === 'uploaded' && (
            <Button
              type="text"
              icon={<SyncOutlined spin={isCheckingStatus} />}
              onClick={() => checkStatus(record.id, {
                onSuccess: () => message.success(t('agents:detail.kb.statusChecked')),
                onError: () => message.error(t('agents:detail.kb.statusCheckFailed')),
              })}
              title={t('agents:detail.kb.checkIndexed')}
            />
          )}
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={isRemovingDoc}
            onClick={() => removeDoc(record.id, {
              onSuccess: () => message.success(t('agents:detail.kb.removed')),
              onError: () => message.error(t('agents:detail.kb.removeFailed')),
            })}
          />
        </Flex>
      ),
    },
  ];

  if (isLoadingAgent) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (!agent) {
    return <p>{t('agents:detail.notFound')}</p>;
  }

  const isPublished = agent.publish_status === 'published';

  const configTab = (
    <Form form={configForm} layout="vertical" onFinish={handleSaveConfig} className={styles.form}>
      <Form.Item
        label={t('agents:labels.name')}
        name="name"
        rules={[{ required: true, message: t('agents:validation.nameRequired') }]}
      >
        <Input className={styles.formInput} />
      </Form.Item>
      <Form.Item label={t('agents:labels.systemPrompt')} name="system_prompt">
        <Input.TextArea className={styles.textareaInput} autoSize={{ minRows: 4, maxRows: 20 }} />
      </Form.Item>
      <Form.Item label={t('agents:labels.firstMessage')} name="first_message">
        <Input className={styles.formInput} placeholder={t('agents:placeholders.firstMessage')} />
      </Form.Item>
      {!agent.multilingual && (
        <Form.Item label={t('agents:labels.language')} name="language">
          <Select options={languageOptions} style={{ width: 200 }} />
        </Form.Item>
      )}
      <Form.Item label={t('agents:labels.voice')} name="voice_id">
        <VoiceField companyId={companyId} />
      </Form.Item>
      {agent.voice_id && voiceHealth?.[agent.voice_id] === 'not_found' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agents:detail.config.voiceNotFound')}
        />
      )}
      <Form.Item
        label={t('agents:detail.config.engine.label')}
        name="eleven_labs_model"
        tooltip={t('agents:detail.config.engine.tooltip')}
      >
        <Select
          allowClear
          placeholder={t('agents:detail.config.engine.placeholder')}
          style={{ width: '100%' }}
          options={modelOptions}
        />
      </Form.Item>
      <Form.Item label={t('agents:labels.phoneNumber')} name="twilio_phone_number_id">
        <Select
          placeholder={t('agents:placeholders.phoneNumber')}
          options={phoneOptions}
          allowClear
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label={t('agents:labels.monthlyBudget')} name="monthly_budget_minutes">
        <InputNumber min={0} style={{ width: 200 }} />
      </Form.Item>
      <Form.Item label={t('agents:labels.budgetAlertThreshold')} name="budget_alert_threshold">
        <InputNumber min={0} max={100} style={{ width: 200 }} />
      </Form.Item>
      <Form.Item label={t('agents:labels.budgetAction')} name="budget_action">
        <Radio.Group>
          <Radio value="alert">{t('agents:budgetActions.alert')}</Radio>
          <Radio value="auto_unpublish">{t('agents:budgetActions.autoUnpublish')}</Radio>
        </Radio.Group>
      </Form.Item>
      <Collapse
        size="small"
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'stt',
            label: t('agents:detail.config.stt.title'),
            children: (
              <>
                <Form.Item
                  label={t('agents:detail.config.stt.vadThreshold')}
                  name="vad_threshold"
                  tooltip={t('agents:detail.config.stt.vadTooltip')}
                >
                  <InputNumber min={0.1} max={1.0} step={0.05} style={{ width: 200 }} />
                </Form.Item>
                <Form.Item
                  label={t('agents:detail.config.stt.minSpeechDuration')}
                  name="min_speech_duration_ms"
                  tooltip={t('agents:detail.config.stt.minSpeechTooltip')}
                >
                  <InputNumber min={50} max={1000} step={50} style={{ width: 200 }} />
                </Form.Item>
              </>
            ),
          },
        ]}
      />
      <Flex gap={8} justify="end">
        <Button buttonVariant="primary" loading={isUpdating} onClick={() => configForm.submit()}>
          {t('agents:detail.config.saveChanges')}
        </Button>
      </Flex>
    </Form>
  );

  const handleTransferAll = () => {
    transferKb({}, {
      onSuccess: (data) => message.success(t('agents:detail.kb.import.imported', { count: data.transferred_count })),
      onError: () => message.error(t('agents:detail.kb.import.failed')),
    });
  };

  const handleTransferSelected = () => {
    if (selectedDocIds.length === 0) return;
    transferKb({ document_ids: selectedDocIds }, {
      onSuccess: (data) => {
        message.success(t('agents:detail.kb.import.imported', { count: data.transferred_count }));
        setSelectedDocIds([]);
      },
      onError: () => message.error(t('agents:detail.kb.import.failed')),
    });
  };

  const transferDocColumns: ColumnsType<TransferableDocument> = [
    { title: t('agents:labels.name'), dataIndex: 'name', key: 'name' },
    { title: t('agents:detail.kb.type'), dataIndex: 'doc_type', key: 'doc_type' },
  ];

  const kbTab = (
    <div className={styles.kbSection}>
      {transferableDocs && transferableDocs.length > 0 && (
        <Collapse
          style={{ marginBottom: 24 }}
          items={[
            {
              key: 'import-kb',
              label: (
                <span>
                  <ImportOutlined style={{ marginRight: 8 }} />
                  {t('agents:detail.kb.import.title', { total: formatNumber(transferableDocs.length) })}
                </span>
              ),
              children: (
                <>
                  <Flex justify="flex-end" gap={8} style={{ marginBottom: 12 }}>
                    <Button
                      size="small"
                      loading={isTransferring}
                      disabled={selectedDocIds.length === 0}
                      onClick={handleTransferSelected}
                    >
                      {t('agents:detail.kb.import.selected', { total: formatNumber(selectedDocIds.length) })}
                    </Button>
                    <Button
                      buttonVariant="primary"
                      size="small"
                      loading={isTransferring}
                      onClick={handleTransferAll}
                    >
                      {t('agents:detail.kb.import.all')}
                    </Button>
                  </Flex>
                  <Table
                    columns={transferDocColumns}
                    dataSource={transferableDocs}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    loading={isLoadingDocs}
                    rowSelection={{
                      selectedRowKeys: selectedDocIds,
                      onChange: (keys) => setSelectedDocIds(keys as number[]),
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      )}

      <Table
        columns={kbColumns}
        dataSource={agent.conversational_agent_documents}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <Modal
        title={viewingDoc?.source_name}
        open={!!viewingDoc}
        onCancel={() => setViewingDoc(null)}
        footer={null}
        width={720}
      >
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 500, overflow: 'auto' }}>
          {viewingDoc?.source_content}
        </pre>
      </Modal>

      <div className={styles.kbAddSection}>
        <h4>{t('agents:detail.kb.addDocument')}</h4>
        <Radio.Group value={kbMode} onChange={(e) => { setKbMode(e.target.value); kbForm.resetFields(); }} style={{ marginBottom: 16 }}>
          <Radio.Button value="text">{t('agents:detail.kb.sourceTypes.text')}</Radio.Button>
          <Radio.Button value="url">{t('agents:detail.kb.sourceTypes.url')}</Radio.Button>
          <Radio.Button value="file">{t('agents:detail.kb.sourceTypes.file')}</Radio.Button>
        </Radio.Group>

        {kbMode === 'file' ? (
          <Upload
            beforeUpload={handleFileUpload}
            showUploadList={false}
            accept=".pdf,.txt,.doc,.docx"
          >
            <Button icon={<UploadOutlined />} loading={isAddingDoc}>
              {t('agents:detail.kb.uploadFile')}
            </Button>
          </Upload>
        ) : (
          <Form form={kbForm} layout="vertical" onFinish={handleAddKbDoc} className={styles.form}>
            <Form.Item label={t('agents:labels.name')} name="kb_name">
              <Input placeholder={t('agents:detail.kb.documentName')} className={styles.formInput} />
            </Form.Item>
            {kbMode === 'text' ? (
              <Form.Item
                label={t('agents:detail.kb.content')}
                name="kb_content"
                rules={[{ required: true, message: t('agents:detail.kb.contentRequired') }]}
              >
                <Input.TextArea
                  className={styles.textareaInput}
                  autoSize={{ minRows: 4, maxRows: 20 }}
                  placeholder={t('agents:detail.kb.contentPlaceholder')}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={t('agents:detail.kb.sourceTypes.url')}
                name="kb_url"
                rules={[{ required: true, message: t('agents:detail.kb.urlRequired') }]}
              >
                <Input placeholder="https://..." className={styles.formInput} />
              </Form.Item>
            )}
            <Button buttonVariant="primary" loading={isAddingDoc} onClick={() => kbForm.submit()}>
              {t('agents:detail.kb.addDocument')}
            </Button>
          </Form>
        )}
      </div>
    </div>
  );

  const usageTab = usage ? (
    <div>
      <div className={styles.usageBar}>
        <p>
          {t('agents:detail.usage.used', {
            used: formatNumber(usage.monthly_usage_minutes, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            }),
            budget:
              typeof usage.monthly_budget_minutes === 'number'
                ? formatNumber(usage.monthly_budget_minutes)
                : '\u221E',
          })}
        </p>
        {usage.monthly_budget_minutes && (
          <Progress percent={Math.min(usage.budget_usage_percent, 100)} status={usage.budget_usage_percent >= 100 ? 'exception' : 'active'} />
        )}
      </div>
      <Table
        dataSource={usage.daily_breakdown}
        rowKey="date"
        pagination={false}
        size="small"
        columns={[
          { title: t('agents:detail.usage.columns.date'), dataIndex: 'date', key: 'date' },
          { title: t('agents:detail.usage.columns.minutes'), dataIndex: 'total_minutes', key: 'total_minutes' },
          { title: t('agents:detail.usage.columns.calls'), dataIndex: 'call_count', key: 'call_count' },
        ]}
      />
    </div>
  ) : (
    <p>{t('agents:detail.usage.empty')}</p>
  );

  const testCallTab = (
    <div className={styles.testCallSection}>
      <Form form={testCallForm} layout="vertical" onFinish={handleTestCall}>
        <Form.Item
          label={t('agents:labels.phoneNumber')}
          name="phone_number"
          rules={[{ required: true, message: t('agents:detail.testCall.phoneRequired') }]}
        >
          <Input placeholder="+1234567890" className={styles.formInput} />
        </Form.Item>
        <Button buttonVariant="primary" loading={isCalling} onClick={() => testCallForm.submit()}>
          {t('agents:detail.testCall.start')}
        </Button>
      </Form>
    </div>
  );

  const handleSaveLanguages = () => {
    if (!localLanguages.length) return;
    updateLanguages(
      { languages: localLanguages.map((lv) => ({ lang_code: lv.lang_code, enabled: lv.enabled, voice_id: lv.voice_id || null })) },
      { onSuccess: () => message.success(t('agents:detail.languages.saved')) }
    );
  };

  const updateLocalLanguage = (langCode: string, updates: Partial<LanguageVoice>) => {
    setLocalLanguages((prev) => prev.map((lv) => (lv.lang_code === langCode ? { ...lv, ...updates } : lv)));
  };


  const handleToggleMultilingual = (checked: boolean) => {
    update(
      { multilingual: checked },
      {
        onSuccess: () =>
          message.success(
            checked
              ? t('agents:detail.languages.multilingualEnabled')
              : t('agents:detail.languages.multilingualDisabled')
          ),
      }
    );
  };

  const engineLabel = agent.eleven_labs_model
    ? t(`agents:models.${agent.eleven_labs_model}`, { defaultValue: agent.eleven_labs_model })
    : t('agents:detail.languages.companyDefaultEngine');

  const languagesTab = (
    <div>
      {/* Engine summary banner — voices and engine are intertwined (a voice
          rendered through Flash sounds different than through Multilingual V2),
          so showing the active engine here saves a tab-trip when admins are
          tuning per-language voices. The selector itself lives on the General
          tab to keep the form-binding tidy. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <Flex align="center" justify="space-between" gap={8}>
            <span>
              <strong>{t('agents:detail.languages.ttsEngine')}</strong> {engineLabel}
            </span>
            <Button size="small" onClick={() => setActiveTab('config')}>
              {t('agents:detail.languages.changeInConfig')}
            </Button>
          </Flex>
        }
      />
      <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
        <Switch
          checked={agent.multilingual}
          onChange={handleToggleMultilingual}
          loading={isUpdating}
        />
        <span style={{ fontWeight: 500 }}>{t('agents:detail.multilingualMode')}</span>
        <span style={{ color: '#888' }}>{t('agents:detail.languages.multilingualDescription')}</span>
      </Flex>
      {agent.multilingual && localLanguages.length > 0 ? (
        <>
          <p style={{ marginBottom: 8, color: '#888' }}>
            {t('agents:detail.languages.assignVoices')}
          </p>
          {/* Workspace context: when admins start hitting clone-from-library
              failures, "your workspace already has 51 voices" is usually the
              answer. Shown only when we have the count from the health probe. */}
          {workspaceTotal !== undefined && (
            <p style={{ marginBottom: 16, color: '#888', fontSize: 12 }}>
              {t('agents:detail.languages.workspaceVoices', { count: workspaceTotal })}
              {voiceHealthError ? ` ${t('agents:detail.languages.healthUnavailable')}` : ''}
            </p>
          )}
          <Table
            dataSource={localLanguages}
            rowKey="lang_code"
            pagination={false}
            size="small"
            columns={[
              { title: t('agents:labels.language'), dataIndex: 'lang_code', key: 'lang_code', width: 200 },
              {
                title: t('agents:detail.languages.enabled'),
                dataIndex: 'enabled',
                key: 'enabled',
                width: 100,
                render: (enabled: boolean, record: LanguageVoice) => (
                  <Switch
                    checked={enabled}
                    onChange={(checked) => updateLocalLanguage(record.lang_code, { enabled: checked })}
                  />
                ),
              },
              {
                title: t('agents:labels.voice'),
                dataIndex: 'voice_id',
                key: 'voice_id',
                render: (_: string, record: LanguageVoice) => (
                  <Flex align="center" gap={8}>
                    <VoiceField
                      value={record.voice_id || null}
                      onChange={(voiceId) => updateLocalLanguage(record.lang_code, { voice_id: voiceId || '' })}
                      companyId={companyId}
                      // Pre-filter the library to this row's ISO language so
                      // browsing the German row doesn't show every voice in the
                      // catalog. Falls back to the human label as a search-text
                      // hint if iso_code is missing on older payloads.
                      defaultLanguage={record.iso_code}
                      defaultSearch={record.iso_code ? undefined : record.lang_code.split(' ')[0]}
                    />
                    {/* Health badge: catches stale voice IDs (deleted from
                        ElevenLabs, or library IDs that never got cloned)
                        BEFORE they cause a runtime 1008 rejection mid-call. */}
                    {record.voice_id && voiceHealth?.[record.voice_id] === 'not_found' && (
                      <Tag color="red">{t('agents:detail.languages.notInWorkspace')}</Tag>
                    )}
                    {record.voice_id && voiceHealth?.[record.voice_id] === 'unknown' && (
                      <Tag color="default">{t('agents:detail.languages.healthUnknown')}</Tag>
                    )}
                  </Flex>
                ),
              },
            ]}
          />
          <Flex justify="end" style={{ marginTop: 16 }}>
            <Button buttonVariant="primary" loading={isSavingLanguages} onClick={handleSaveLanguages}>
              {t('agents:detail.languages.save')}
            </Button>
          </Flex>
        </>
      ) : agent.multilingual ? (
        <Spin />
      ) : null}
    </div>
  );

  const enabledLangCount = localLanguages.filter((lv) => lv.enabled && lv.voice_id).length;
  const status = agent.integration_status;

  const StatusTag = ({ ok }: { ok: boolean }) => (
    <Tag color={ok ? 'green' : 'default'}>
      {ok ? t('agents:detail.integration.status.ok') : t('agents:detail.integration.status.missing')}
    </Tag>
  );

  const integrationTab = (
    <Flex vertical gap={16}>
      <h4 style={{ margin: 0 }}>{t('agents:detail.integration.title')}</h4>
      <Table
        dataSource={[
          {
            key: 'agent_id',
            setting: t('agents:detail.integration.rows.agentId'),
            value: status?.eleven_labs_agent_id || t('agents:detail.integration.values.notPublished'),
            status: status?.published,
          },
          {
            key: 'post_call_webhook',
            setting: t('agents:detail.integration.rows.postCallWebhook'),
            value: status?.post_call_webhook_id || t('agents:detail.integration.values.notConfigured'),
            status: status?.post_call_webhook,
          },
          {
            key: 'webhook_secret',
            setting: t('agents:detail.integration.rows.webhookSecret'),
            value: status?.webhook_secret_set
              ? t('agents:detail.integration.values.secretSet')
              : t('agents:detail.integration.values.secretNotSet'),
            status: status?.webhook_secret_set,
          },
          {
            key: 'initiation_webhook',
            setting: t('agents:detail.integration.rows.initiationWebhook'),
            value:
              status?.initiation_webhook_url ||
              t('agents:detail.integration.values.initiationMissingHost'),
            status: status?.initiation_webhook,
          },
          {
            key: 'monitoring',
            setting: t('agents:detail.integration.rows.monitoring'),
            value: t('agents:detail.integration.values.monitoringOn'),
            status: status?.monitoring_enabled,
          },
          {
            key: 'multilingual',
            setting: t('agents:detail.multilingualMode'),
            value: status?.multilingual
              ? t('agents:detail.integration.values.multilingualOn', {
                  languages: formatNumber(status.language_count),
                })
              : t('agents:detail.integration.values.disabled'),
            status: status?.multilingual ? status.language_count > 0 : undefined,
          },
        ]}
        columns={[
          { title: t('agents:detail.integration.columns.setting'), dataIndex: 'setting', key: 'setting', width: 200 },
          { title: t('agents:detail.integration.columns.value'), dataIndex: 'value', key: 'value' },
          {
            title: t('agents:labels.status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (ok: boolean | undefined) =>
              ok === undefined ? (
                <Tag>{t('agents:detail.integration.status.notApplicable')}</Tag>
              ) : (
                <StatusTag ok={ok} />
              ),
          },
        ]}
        pagination={false}
        size="small"
      />
      <p style={{ color: '#666', fontSize: 12, margin: 0 }}>
        {t('agents:detail.integration.footer', { syncButton: t('agents:detail.header.sync') })}
      </p>
    </Flex>
  );

  const announcementReadyCount = announcements?.filter((a) => a.status === 'ready').length ?? 0;
  const announcementTotalCount = announcements?.length ?? 0;
  const fillerReadyCount = fillerClips?.filter((c) => c.status === 'ready').length ?? 0;
  const fillerTotalCount = fillerClips?.length ?? 0;
  const audioReadyCount = announcementReadyCount + fillerReadyCount;
  const audioTotalCount = announcementTotalCount + fillerTotalCount;

  const disclaimerEntry = audioClipTexts?.find((clip) => clip.clip_type === 'disclaimer');
  const transferringEntry = audioClipTexts?.find((clip) => clip.clip_type === 'transferring');
  const fillerEntry = audioClipTexts?.find((clip) => clip.clip_type === 'filler');

  // Named in the dictionary texts that point the user at these two buttons.
  const generateMissingLabel = t('agents:detail.audio.generateMissing');
  const regenerateAllLabel = t('agents:detail.audio.regenerateAll');

  const renderTextEditor = (
    clip_type: AudioClipType,
    entry: AudioClipText | undefined,
    draft: string,
    setDraft: (v: string) => void
  ) => {
    const dirty = entry !== undefined && draft.trim() !== entry.text.trim();
    const clipName = t(`agents:detail.audio.clips.${clip_type}.name`);
    return (
      <Flex vertical gap={6} style={{ marginBottom: 8 }}>
        <Flex align="center" gap={8}>
          <strong>{t(`agents:detail.audio.clips.${clip_type}.label`)}</strong>
          {entry?.customized && <Tag color="blue">{t('agents:detail.audio.tags.customized')}</Tag>}
          {entry && !entry.customized && <Tag>{t('agents:detail.audio.tags.default')}</Tag>}
          {entry?.stale && <Tag color="orange">{t('agents:detail.audio.tags.stale')}</Tag>}
        </Flex>
        <span style={{ color: '#666', fontSize: 12 }}>
          {t(`agents:detail.audio.clips.${clip_type}.description`)}
        </span>
        <Input.TextArea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={entry?.default_text ?? ''}
        />
        <Flex gap={8} align="center" wrap="wrap">
          <Button
            type="primary"
            size="small"
            disabled={!dirty || draft.trim().length === 0}
            loading={isSavingAudioClipText}
            onClick={async () => {
              await saveAudioClipText({ clip_type, text: draft.trim() });
              message.success(
                t('agents:detail.audio.textSaved', { name: clipName, regenerateAll: regenerateAllLabel })
              );
            }}
          >
            {t('agents:detail.audio.saveText')}
          </Button>
          {entry?.customized && (
            <Button
              size="small"
              loading={isResettingAudioClipText}
              onClick={async () => {
                await resetAudioClipText(clip_type);
                if (entry?.default_text) setDraft(entry.default_text);
                message.success(
                  t('agents:detail.audio.textReset', { name: clipName, regenerateAll: regenerateAllLabel })
                );
              }}
            >
              {t('agents:detail.audio.resetToDefault')}
            </Button>
          )}
          <span style={{ color: '#999', fontSize: 11 }}>
            {t('agents:detail.audio.defaultText', { text: entry?.default_text ?? '…' })}
          </span>
        </Flex>
      </Flex>
    );
  };

  const anyStale = (audioClipTexts ?? []).some((clip) => clip.stale);

  const announcementsTab = (
    <Flex vertical gap={16}>
      <h4 style={{ margin: 0 }}>{t('agents:detail.audio.title')}</h4>
      <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
        <Trans
          ns="agents"
          i18nKey="detail.audio.intro"
          values={{ regenerateAll: regenerateAllLabel }}
          components={{ strong: <strong /> }}
        />
      </p>

      {renderTextEditor(
        'disclaimer',
        disclaimerEntry,
        draftDisclaimer,
        setDraftDisclaimer,
      )}

      {renderTextEditor(
        'transferring',
        transferringEntry,
        draftTransferring,
        setDraftTransferring,
      )}

      {anyStale && !isGenerating && (
        <Tag color="orange" style={{ alignSelf: 'flex-start', padding: '4px 12px' }}>
          <Trans
            ns="agents"
            i18nKey="detail.audio.staleNotice"
            values={{ regenerateAll: regenerateAllLabel }}
            components={{ strong: <strong /> }}
          />
        </Tag>
      )}
      <Flex gap={8}>
        <Button
          type={anyStale ? 'primary' : 'default'}
          onClick={() => generateAnnouncements(false)}
          loading={isGenerating}
        >
          {generateMissingLabel}
        </Button>
        <Button
          type={anyStale ? 'primary' : 'default'}
          onClick={() => generateAnnouncements(true)}
          loading={isGenerating}
        >
          {regenerateAllLabel}
        </Button>
        <Button onClick={() => refetchAnnouncements()}>
          <SyncOutlined /> {t('agents:detail.audio.refreshStatus')}
        </Button>
      </Flex>
      {isGenerating && (
        <Tag color="blue" style={{ marginBottom: 8 }}>
          {t('agents:detail.audio.generating')}
        </Tag>
      )}
      <Table
        dataSource={announcements ?? []}
        loading={isLoadingAnnouncements}
        rowKey={(row: TransferAnnouncement) => `${row.clip_type}-${row.language}`}
        size="small"
        pagination={false}
        columns={[
          {
            title: t('agents:detail.audio.clip'),
            dataIndex: 'clip_type',
            key: 'clip_type',
            render: (clip_type: AudioClipType) => (
              <Tag color={clip_type === 'disclaimer' ? 'purple' : 'cyan'}>
                {clip_type === 'disclaimer'
                  ? t('agents:detail.audio.clips.disclaimer.name')
                  : t('agents:detail.audio.clips.transferring.name')}
              </Tag>
            ),
          },
          {
            title: t('agents:labels.language'),
            dataIndex: 'language',
            key: 'language',
            render: (lang: string) => (
              <span>{languageName(lang)} <span style={{ color: '#999', fontSize: 11 }}>({lang})</span></span>
            ),
          },
          {
            title: t('agents:labels.voice'),
            dataIndex: 'voice_id',
            key: 'voice_id',
            render: (vid: string | null, record: TransferAnnouncement) =>
              vid
                ? (
                  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {vid.slice(0, 12)}...
                    {' '}<Tag color={record.source === 'company' ? 'blue' : 'default'}>
                      {t(`agents:detail.audio.sources.${record.source ?? 'none'}`)}
                    </Tag>
                  </span>
                )
                : <Tag>{t('agents:detail.audio.noVoice')}</Tag>,
          },
          {
            title: t('agents:labels.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) =>
              status === 'ready' ? (
                <Tag color="green">{t('agents:detail.audio.clipStatus.ready')}</Tag>
              ) : (
                <Tag color="orange">{t('agents:detail.audio.clipStatus.missing')}</Tag>
              ),
          },
          {
            title: '',
            key: 'preview',
            width: 60,
            render: (_: unknown, record: TransferAnnouncement) =>
              record.status === 'ready' && record.url ? (
                <Button
                  size="small"
                  icon={<SoundOutlined />}
                  onClick={() => { new Audio(record.url!).play(); }}
                />
              ) : null,
          },
        ]}
      />

      <h4 style={{ margin: '24px 0 0' }}>{t('agents:detail.audio.fillerTitle')}</h4>
      <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
        <Trans
          ns="agents"
          i18nKey="detail.audio.fillerIntro"
          values={{ generateMissing: generateMissingLabel, regenerateAll: regenerateAllLabel }}
          components={{ strong: <strong /> }}
        />
      </p>

      {renderTextEditor(
        'filler',
        fillerEntry,
        draftFiller,
        setDraftFiller,
      )}

      <Flex gap={8}>
        <Button onClick={() => refetchFillerClips()}>
          <SyncOutlined /> {t('agents:detail.audio.refreshStatus')}
        </Button>
      </Flex>
      <Table
        dataSource={fillerClips ?? []}
        loading={isLoadingFillerClips}
        rowKey={(row: FillerClip) => `${row.voice_id}-${row.lang_code}`}
        size="small"
        pagination={false}
        columns={[
          {
            title: t('agents:labels.language'),
            dataIndex: 'lang_code',
            key: 'lang_code',
            render: (lang: string) => (
              <span>
                {languageName(lang)}{' '}
                <span style={{ color: '#999', fontSize: 11 }}>({lang})</span>
              </span>
            ),
          },
          {
            title: t('agents:labels.voice'),
            dataIndex: 'voice_id',
            key: 'voice_id',
            render: (vid: string) => (
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{vid.slice(0, 12)}...</span>
            ),
          },
          {
            title: t('agents:labels.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string, row: FillerClip) =>
              status === 'ready' ? (
                <Tag color="green">{t('agents:detail.audio.clipStatus.ready')}</Tag>
              ) : (
                <Tag color="orange">
                  {row.reason
                    ? t('agents:detail.audio.clipStatus.missingWithReason', { reason: row.reason })
                    : t('agents:detail.audio.clipStatus.missing')}
                </Tag>
              ),
          },
          {
            title: '',
            key: 'preview',
            width: 60,
            render: (_: unknown, record: FillerClip) =>
              record.status === 'ready' ? (
                <FillerClipPreviewButton
                  agentId={agentId}
                  companyId={companyId!}
                  voiceId={record.voice_id}
                  langCode={record.lang_code}
                />
              ) : null,
          },
        ]}
      />
    </Flex>
  );

  const tabItems = [
    { key: 'config', label: t('agents:detail.tabs.config'), children: configTab },
    {
      key: 'kb',
      label: t('agents:detail.tabs.knowledgeBase', {
        total: formatNumber(agent.conversational_agent_documents?.length || 0),
      }),
      children: kbTab,
    },
    {
      key: 'languages',
      label: enabledLangCount
        ? t('agents:detail.tabs.languagesWithCount', { total: formatNumber(enabledLangCount) })
        : t('agents:detail.tabs.languages'),
      children: languagesTab,
    },
    { key: 'integration', label: t('agents:detail.tabs.integration'), children: integrationTab },
    ...(isPublished
      ? [
          { key: 'usage', label: t('agents:detail.tabs.usage'), children: usageTab },
          { key: 'test-call', label: t('agents:detail.tabs.testCall'), children: testCallTab },
          {
            key: 'announcements',
            label: t('agents:detail.tabs.audioClips', {
              ready: formatNumber(audioReadyCount),
              total: formatNumber(audioTotalCount),
            }),
            children: announcementsTab,
          },
        ]
      : []),
  ];

  return (
    <Flex vertical gap={16} style={{ width: '100%' }}>
      <div className={styles.headerSection}>
        <div className={styles.headerLeft}>
          <h2 className={styles.agentName}>{agent.name}</h2>
          <AgentStatusBadge status={agent.publish_status} />
        </div>
        <div className={styles.headerActions}>
          {isPublished ? (
            <>
              <Button
                buttonVariant="primary"
                loading={isSyncing}
                onClick={() => sync(undefined, {
                  onSuccess: () => message.success(t('agents:detail.messages.synced')),
                  onError: () => message.error(t('agents:detail.messages.syncFailed')),
                })}
              >
                {t('agents:detail.header.sync')}
              </Button>
              <Button buttonVariant="secondary" loading={isUnpublishing} onClick={handleUnpublish}>
                {t('agents:detail.header.unpublish')}
              </Button>
            </>
          ) : (
            <Button buttonVariant="primary" loading={isPublishing} onClick={handlePublish}>
              {t('agents:detail.header.publish')}
            </Button>
          )}
          <Button danger loading={isDeleting} onClick={handleDelete}>
            {t('common:actions.delete')}
          </Button>
        </div>
      </div>

      {agent.publish_error && <p className={styles.errorText}>
          {t('agents:detail.header.publishError', { error: agent.publish_error })}
        </p>}

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Flex>
  );
}
